import { Stack } from '@mui/material';
import { useIsMobile } from '@side/pantry';
import {
  ControlPosition,
  Map as GoogleMap,
  MapControl,
  useMap,
} from '@vis.gl/react-google-maps';
import { useSetAtom } from 'jotai';
import { type Dictionary, debounce } from 'lodash';
import { type MouseEvent, useState } from 'react';
import {
  selectedGroupKey,
  selectedPartnerMapTeamId,
  teamDetailPanelVisible,
} from '@/common/atoms/community';
import { Spinner } from '@/components/Spinner';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { DEFAULT_ZOOM } from '../../constants';
import type { InitialLocation, TeamSearchResult } from '../../types';
import { MapListToggle } from '../MapListToggle';
import { Markers } from '../Markers';
import { MobilePartnerCardList } from '../MobilePartnerCardList';
import { PartnerDetailsPanel } from '../PartnerDetailsPanel';
import { ShowAllPartnersButton } from '../ShowAllPartnersButton';
import { TeamLocationsStepper } from '../TeamLocationsStepper';

interface Props {
  getTeams: (
    map: google.maps.Map | null,
    initialCenter: InitialLocation,
  ) => Promise<void>;
  loading: boolean;
  partnerGroups: Dictionary<TeamSearchResult[]>;
  initialCenter: InitialLocation;
}

export const MapRenderer = ({
  getTeams,
  loading,
  partnerGroups,
  initialCenter,
}: Props) => {
  const [showMobilePartnerList, setShowMobilePartnerList] = useState(false);
  const isMobile = useIsMobile('md');
  const trackEvent = useTrackCommunityAnalytics();
  const setSelectedTeamId = useSetAtom(selectedPartnerMapTeamId);
  const setTeamDetailPanelVisible = useSetAtom(teamDetailPanelVisible);
  const setSelectedGroup = useSetAtom(selectedGroupKey);
  const mapId = process.env.NEXT_PUBLIC_GOOGLE_MAP_ID;
  const map = useMap();

  const handleMapClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element) {
      const clickWithinMap = !!e.target.closest(
        '[data-testid="map"] > div:first-of-type',
      );
      const pinClicked = !!e.target.closest('.pin');
      if (clickWithinMap && !pinClicked) {
        setSelectedTeamId(undefined);
        setTeamDetailPanelVisible(false);
        setSelectedGroup(undefined);
      }
    }
  };

  return (
    <Stack
      sx={{ width: '100%', position: 'relative' }}
      onClick={handleMapClick}
    >
      <GoogleMap
        style={{ opacity: loading && isMobile ? 0.4 : 1 }}
        mapId={mapId}
        defaultCenter={initialCenter}
        defaultZoom={DEFAULT_ZOOM}
        onBoundsChanged={debounce(() => void getTeams(map, initialCenter), 300)}
        onDragend={(e) => {
          trackEvent(TrackCommunityEvent.PartnerMapDrag, {
            zoomGranularity: e.map.getZoom(),
            geoValue: [e.map.getCenter()?.lat(), e.map.getCenter()?.lng()],
          });
        }}
        onZoomChanged={(e) => {
          trackEvent(TrackCommunityEvent.PartnerMapZoomChange, {
            zoomGranularity: e.map.getZoom(),
            geoValue: [e.map.getCenter()?.lat(), e.map.getCenter()?.lng()],
          });
        }}
        cameraControl={false}
        fullscreenControl={false}
        mapTypeControl={false}
        streetViewControl={false}
        zoomControl
      >
        <PartnerDetailsPanel />
        <TeamLocationsStepper />
        <Markers map={map} partnerGroups={partnerGroups} />
        <MobilePartnerCardList
          isEmpty={!Object.entries(partnerGroups).length && !loading}
          showList={showMobilePartnerList}
          partnerGroups={partnerGroups}
          loading={loading}
        />
        <MapListToggle
          showList={showMobilePartnerList}
          toggleVisibility={() =>
            setShowMobilePartnerList(!showMobilePartnerList)
          }
        />
        <MapControl position={ControlPosition.RIGHT_BOTTOM}>
          <ShowAllPartnersButton />
        </MapControl>
      </GoogleMap>
      {loading && isMobile && (
        <Spinner
          centered
          sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        />
      )}
    </Stack>
  );
};
